import { Button, SvgIcon, Tooltip, useTheme } from "@mui/material";
import { CategorizationMap } from "portal/utils/categoryCollection/category";
import { ChipOverlay } from "./imageOverlays/ChipOverlay";
import {
  Dimensions,
  getPlantAwareCropping,
} from "portal/utils/categoryCollection/image";
import { ImageGrid, OptionalImageGridItem } from "../images/ImageGrid";
import { ImageProcessingOverlay } from "./imageOverlays/ImageProcessingOverlay";
import { isAbort } from "portal/utils/errors";
import { isEqual } from "common/utils/equality";
import { LoadMoreImagesButton } from "./LoadMoreImagesButton";
import { PaddingDerivedPointLabelOverlay } from "./imageOverlays/PointLabelOverlay";
import { PageSortParams } from "protos/portal/veselka";
import { PolaroidOverlay } from "./imageOverlays/PolaroidOverlay";
import { portalApi } from "portal/state/portalApi";
import {
  PredictionPoint,
  SessionStatus,
} from "protos/veselka/prediction_point";
import { PredictionPointExpandedImage } from "./expandedImage/PredictionPointExpandedImage";
import {
  processImageResults,
  ResultsMetadata,
} from "../images/processImageResults";
import { RadioChipOption } from "../RadioChips";
import { STALE_CHIP_COLOR } from "portal/theme/theme";
import { ThumbnailImageItem } from "../images/ThumbnailImage";
import { UiPredictionPointFilters } from "portal/utils/categoryCollection/predictionPoints";
import {
  UploadedByOperatorIcon,
  UploadedByOperatorImageOverlay,
} from "./imageOverlays/IconOverlay";
import { useInfiniteScroll } from "portal/utils/hooks/useInfiniteScroll";
import { useLazyPopups } from "portal/utils/hooks/useApiPopups";
import { useNavigateExpandedImage } from "../images/useNavigateExpandedImage";
import { usePageRef } from "../Page";
import { useScrollToImage } from "./useScrollToImage";
import { useToPredictionPointFilters } from "./filters/useToPredictionPointFilters";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ModelIcon from "portal/images/icons/model.svg?react";
import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import TimeIcon from "@mui/icons-material/AccessTime";

export type ThumbnailBaseInfo = Pick<
  ThumbnailImageItem,
  "id" | "x" | "y" | "width" | "height" | "url"
> & {
  radius: number;
  ppcm: number;
};

interface ImageCategorizationOptionsProps {
  scopeCustomerUuid?: string;
  session?: {
    processing: boolean;
    stale: boolean;
    status?: SessionStatus;
  };
  areFilterResultsIncomplete: boolean;
  filters: UiPredictionPointFilters;
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  dimensions: Dimensions;
  onImageClick?: (predictionPointId: string) => void;
  onClassify: (predictionPointId: string, categoryId: string) => void;
  onRefreshOptions?: () => void;
}
const DEFAULT_PAGINATION: PageSortParams = { page: 1, pageSize: 50 };

const initialResultsState: ResultsMetadata<PredictionPoint> = {
  data: {
    ids: new Set(),
    data: [],
  },
  areAllResultsLoaded: false,
  loadableImagesCount: 0,
};

export const ImageCategorizationOptions: FC<
  ImageCategorizationOptionsProps
> = ({
  scopeCustomerUuid,
  session,
  filters,
  areFilterResultsIncomplete,
  categoryOptions,
  categorizationMap,
  dimensions,
  onImageClick,
  onClassify,
  onRefreshOptions,
}) => {
  const userPreferences = useUserPreferences();
  const [expandedImage, setExpandedImage] = useState<
    { index: number; data: PredictionPoint } | undefined
  >();
  const [scrollToImage, setScrollToImage] = useState<
    { id?: string; visible?: boolean } | undefined
  >();
  const { onScrollToImage } = useScrollToImage({
    scrollToImage,
    setScrollToImage,
  });

  const [imagesRendered, setImagesRendered] = useState(0);
  const [pagination, setPagination] =
    useState<PageSortParams>(DEFAULT_PAGINATION);
  const [resultsState, setResultsState] =
    useState<ResultsMetadata<PredictionPoint>>(initialResultsState);

  const { toPredictionPointFilters } =
    useToPredictionPointFilters(scopeCustomerUuid);
  const [getPredictionPoints] = useLazyPopups(
    portalApi.endpoints.listPredictionPoints.useLazyQuery()
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const previousFetchResultsRef = useRef<ReturnType<
    typeof getPredictionPoints
  > | null>(null);
  const fetchIdRef = useRef(0);

  const fetchResults = useCallback(
    async (
      pagination: PageSortParams,
      {
        crops,
        robots,
        capturedAt,
        radiusUserUnits,
        sessionId,
        categoryIds,
        sessionCropIds,
        uploadedByOperator,
        plantProfileCategoryId,
      }: UiPredictionPointFilters
    ): Promise<void> => {
      // Cancel the previous request if it's still active so the previous filters' results don't get added to current results
      previousFetchResultsRef.current?.abort();
      const fetchId = ++fetchIdRef.current;

      setIsLoading(true);
      setIsError(false);
      try {
        const query = getPredictionPoints(
          toPredictionPointFilters(
            {
              crops,
              robots,
              radiusUserUnits,
              capturedAt,
              uploadedByOperator,
              sessionId,
              categoryIds,
              sessionCropIds,
              plantProfileCategoryId,
            },
            pagination
          )
        );
        previousFetchResultsRef.current = query;
        const predictionPointResults = await query.unwrap();
        if (fetchId !== fetchIdRef.current) {
          return;
        }
        setResultsState((previous) =>
          processImageResults(previous, predictionPointResults)
        );
        setPagination((previous) => ({
          ...previous,
          page: previous.page + 1,
        }));
      } catch (error) {
        if (isAbort(error)) {
          // Request was cancelled — ignore error; do not clear loading for a newer fetch
          return;
        }
        if (fetchId === fetchIdRef.current) {
          setIsError(true);
        }
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [toPredictionPointFilters, getPredictionPoints]
  );

  // Automatically fetch more results when infinite scrolling
  const loadMore = useCallback(() => {
    if (
      !isLoading &&
      !resultsState.areAllResultsLoaded &&
      !isError &&
      imagesRendered >= resultsState.loadableImagesCount
    ) {
      fetchResults(pagination, filters);
    }
  }, [
    isLoading,
    resultsState.areAllResultsLoaded,
    resultsState.loadableImagesCount,
    isError,
    imagesRendered,
    fetchResults,
    pagination,
    filters,
  ]);

  const loadMoreRef = useInfiniteScroll<HTMLDivElement>(
    isLoading || imagesRendered < resultsState.loadableImagesCount,
    loadMore
  );

  const resetResults = useCallback(() => {
    setResultsState(initialResultsState);
    setPagination(DEFAULT_PAGINATION);
    setImagesRendered(0);
    fetchResults(DEFAULT_PAGINATION, filters);
    onRefreshOptions?.();
  }, [fetchResults, filters, onRefreshOptions]);

  // Reset results and refetch when filters or customer scope change.
  const prevFilterRef = useRef<UiPredictionPointFilters>();
  const prevScopeCustomerUuidRef = useRef<string | undefined>();
  useEffect(() => {
    if (
      isEqual(filters, prevFilterRef.current) &&
      scopeCustomerUuid === prevScopeCustomerUuidRef.current
    ) {
      return;
    }
    prevFilterRef.current = filters;
    prevScopeCustomerUuidRef.current = scopeCustomerUuid;
    resetResults();
  }, [filters, scopeCustomerUuid, resetResults]);
  const thumbnailImages: OptionalImageGridItem[] = useMemo(
    () =>
      resultsState.data.data.map((data, i) => {
        const { id, x, y, radius, image, categoryId } = data;
        if (!image) {
          return;
        }
        const thumbnailCropPlacement = getPlantAwareCropping({
          x,
          y,
          radius,
        });
        let category: RadioChipOption | undefined;
        let predictedCategory: RadioChipOption | undefined;
        for (const option of categoryOptions) {
          if (option.id === categorizationMap[id]) {
            category = option;
          }
          if (option.id === categoryId) {
            predictedCategory = option;
          }
        }
        const baseThumbnail = {
          id,
          url: image.url,
          x: thumbnailCropPlacement.x,
          y: thumbnailCropPlacement.y,
          width: thumbnailCropPlacement.width,
          height: thumbnailCropPlacement.height,
          radius,
          ppcm: image.ppcm,
          capturedAt: image.capturedAt,
          className:
            id === scrollToImage?.id && scrollToImage.visible
              ? "animate-emphasize"
              : undefined,
        };

        const predictionChipIconProps = {
          className: "w-3 h-3",
          style: {
            color: session?.stale
              ? STALE_CHIP_COLOR.text
              : predictedCategory?.color?.text,
          },
        };

        return {
          ...baseThumbnail,
          onClick: () => {
            onImageClick?.(data.id);
          },
          onDoubleClick: () => {
            setExpandedImage({ index: i, data });
          },
          renderOverlay: () => {
            const showLabels = userPreferences.categoryCollection.showLabels;
            const radiusCm = image.ppcm > 0 ? radius / image.ppcm : undefined;
            const showPrediction = predictedCategory && !category;
            const uploadedByOperator = data.image?.uploadedByOperator;

            return (
              <>
                {session?.processing && !showPrediction && (
                  <ImageProcessingOverlay image={baseThumbnail} />
                )}
                {showLabels && (
                  <PaddingDerivedPointLabelOverlay
                    padding={
                      dimensions.width * thumbnailCropPlacement.paddingFactor
                    }
                    radiusCm={category ? undefined : radiusCm}
                    dimensions={dimensions}
                  />
                )}
                {uploadedByOperator && !showPrediction ? (
                  <UploadedByOperatorImageOverlay />
                ) : undefined}
                {showPrediction && predictedCategory && (
                  <ChipOverlay
                    name={predictedCategory.name}
                    color={
                      session?.stale
                        ? STALE_CHIP_COLOR
                        : predictedCategory.color
                    }
                    icon={
                      session?.stale ? (
                        <TimeIcon {...predictionChipIconProps} />
                      ) : (
                        <>
                          {uploadedByOperator && (
                            <UploadedByOperatorIcon
                              style={{ color: predictedCategory.color?.text }}
                              className="text-lg"
                            />
                          )}
                          <SvgIcon {...predictionChipIconProps}>
                            <ModelIcon />
                          </SvgIcon>
                        </>
                      )
                    }
                  />
                )}
                {category && (
                  <PolaroidOverlay
                    dimensions={dimensions}
                    radiusCm={showLabels ? radiusCm : undefined}
                    name={category.name}
                    color={category.color}
                    position="bottom"
                  />
                )}
              </>
            );
          },
        };
      }),
    [
      session,
      categorizationMap,
      categoryOptions,
      dimensions,
      onImageClick,
      resultsState.data.data,
      scrollToImage,
      userPreferences.categoryCollection.showLabels,
    ]
  );

  const onChangeImage = useNavigateExpandedImage<PredictionPoint>(
    resultsState.data.data,
    setExpandedImage,
    expandedImage
  );

  return (
    <>
      <PredictionPointExpandedImage
        open={Boolean(expandedImage)}
        onClose={() => {
          setScrollToImage({ id: expandedImage?.data.id });
          setExpandedImage(undefined);
        }}
        onExited={onScrollToImage}
        onNext={onChangeImage(1)}
        onBack={onChangeImage(-1)}
        predictionPoint={expandedImage?.data}
        categoryOptions={categoryOptions}
        categorizationMap={categorizationMap}
        categoryId={
          expandedImage?.data.id
            ? categorizationMap[expandedImage.data.id]
            : undefined
        }
        isSessionStale={session?.stale}
        onClassify={onClassify}
      />
      <ImageGrid
        loading={isLoading}
        loadingItemCount={pagination.pageSize}
        dimensions={dimensions}
        thumbnailImages={thumbnailImages}
        onImagesLoaded={setImagesRendered}
      />
      <div className="flex justify-center" ref={loadMoreRef}>
        <LoadMoreImagesButton
          isInitialRequestLoading={isLoading}
          canLoadMore={isLoading || !resultsState.areAllResultsLoaded}
          onLoadMore={() => fetchResults(pagination, filters)}
          onRefresh={resetResults}
          areFilterResultsIncomplete={areFilterResultsIncomplete}
          sessionStatus={session?.status}
        />
      </div>
    </>
  );
};

export const ScrollableImageCategorizationOptions: FC<
  ImageCategorizationOptionsProps & {
    floatingAction?: ReactNode;
  }
> = ({
  scopeCustomerUuid,
  session,
  filters,
  areFilterResultsIncomplete,
  dimensions,
  categoryOptions,
  categorizationMap,
  onImageClick,
  onClassify,
  onRefreshOptions,
  floatingAction,
}) => {
  const pageRef = usePageRef();
  const { t } = useTranslation();
  const muiTheme = useTheme();

  const imageOptionsRef = useRef<HTMLDivElement>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  const onScroll = useCallback(() => {
    const imagesElement = imageOptionsRef.current;
    if (!imagesElement || !pageRef?.current) {
      return;
    }

    const pageRect = pageRef.current.getBoundingClientRect();
    const imagesRect = imagesElement.getBoundingClientRect();

    const scrollPosRelativeToImages = pageRect.top - imagesRect.top;
    const SCROLL_BUFFER_PX = 100;
    setShowScrollToTop(scrollPosRelativeToImages > SCROLL_BUFFER_PX);
  }, [pageRef]);

  useEffect(() => {
    const el = pageRef?.current;
    if (!el) {
      return;
    }
    el.addEventListener("scroll", onScroll);
    return () => {
      el.removeEventListener("scroll", onScroll);
    };
  }, [onScroll, pageRef]);

  const handleScrollToTop = (): void => {
    const imagesElement = imageOptionsRef.current;
    if (!imagesElement || !pageRef?.current) {
      return;
    }

    const { top } = pageRef.current.getBoundingClientRect();
    const { scrollHeight } = pageRef.current;
    pageRef.current.scrollTo({
      top,
      behavior: scrollHeight > 20_000 ? "auto" : "smooth",
    });
  };

  return (
    <>
      <div ref={imageOptionsRef}>
        <ImageCategorizationOptions
          scopeCustomerUuid={scopeCustomerUuid}
          filters={filters}
          areFilterResultsIncomplete={areFilterResultsIncomplete}
          dimensions={dimensions}
          categoryOptions={categoryOptions}
          categorizationMap={categorizationMap}
          onImageClick={onImageClick}
          session={session}
          onClassify={onClassify}
          onRefreshOptions={onRefreshOptions}
        />
      </div>
      {showScrollToTop && (
        <div className="flex justify-end">
          <div className="fixed bottom-4 z-20 flex items-center gap-2">
            {floatingAction}
            <Tooltip
              title={t(
                "components.categoryCollectionProfile.images.scrollToTop"
              )}
              arrow
            >
              <Button
                aria-label={t(
                  "components.categoryCollectionProfile.images.scrollToTop"
                )}
                className="min-w-0 p-2 sm:px-3 rounded shadow gap-2"
                style={{
                  background:
                    muiTheme.palette.mode === "light"
                      ? muiTheme.palette.background.section
                      : muiTheme.palette.common.white,
                }}
                onClick={handleScrollToTop}
              >
                <ArrowUpwardIcon />
                <span className="hidden sm:inline">
                  {t("components.categoryCollectionProfile.images.scrollToTop")}
                </span>
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </>
  );
};
